import { useState, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { BusinessIdeaPDF } from './components/BusinessResultPDF/BusinessResultPDF';
import IdeasPanel from './components/IdeasPanel/IdeasPanel';
import ReportPanel from './components/ReportPanel/ReportPanel';
import { BusinessResultsData } from './types';

/**
 * Business Results component - displays personalized business ideas and detailed reports
 * Replaces ChoiceFlow after form submission to show generated business recommendations
 * @param {object} props - Component props
 * @param {object} props.data - Business results data containing ideas and user profile
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @component
 */
function BusinessResults({ data, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { answers, isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();
  const [selectedIdeaId, setSelectedIdeaId] = useState(data.selectedIdeaId || data.ideas[0]?.id);

  // Find the currently selected idea
  const selectedIdea = useMemo(
    () => data.ideas.find((idea) => idea.id === selectedIdeaId),
    [data.ideas, selectedIdeaId]
  );

  const handleIdeaSelect = (ideaId) => {
    setSelectedIdeaId(ideaId);
  };

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      // const selectedIdea = data.business_ideas.find((idea) => idea.id === selectedIdeaId);
      const blob = await pdf(
        <BusinessIdeaPDF environment={DEPLOY_ENV} idea={selectedIdea} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedIdea.title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  // const handleEmailReport = async (ideaId) => {
  //   if (!ideaId || emailLoading) return;

  //   setEmailLoading(true);

  //   try {
  //     if (onEmailReport) {
  //       await onEmailReport(ideaId);
  //     } else if (onComponentClick) {
  //       // Follow assistant-ui pattern for component actions
  //       onComponentClick({
  //         type: 'business_results_email',
  //         payload: {
  //           action: 'email_report',
  //           ideaId,
  //           messageType: 'business_report_email'
  //         }
  //       });
  //     }
  //   } catch (error) {
  //     console.error('[BusinessResults] Email report error:', error);
  //   } finally {
  //     setEmailLoading(false);
  //   }
  // };

  const handleGenerateMore = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await onComponentClick({
        type: 'business_results_generate_more',
        payload: {
          answers
        }
      });
    }
    setLoading(false);
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no ideas are provided
  if (!data.ideas || data.ideas.length === 0) {
    return (
      <Styled.BusinessResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No business ideas found</Text>
          <Text>
            We weren't able to generate business ideas based on your responses. Please try again.
          </Text>
        </Box>
      </Styled.BusinessResultsRoot>
    );
  }

  return (
    <Styled.BusinessResultsRoot>
      <Styled.IdeasHeader>
        <Text size="md" weight="medium">
          Your personalized business ideas
        </Text>
        <Text size="sm">
          Based on your responses, here are business ideas tailored specifically for you.
        </Text>
      </Styled.IdeasHeader>
      <Styled.IdeasContent>
        <IdeasPanel
          ideas={data.ideas}
          loading={isLoading}
          selectedIdeaId={selectedIdeaId}
          onGenerateMore={handleGenerateMore}
          onIdeaSelect={handleIdeaSelect}
          onStartOver={handleStartOver}
        />

        <ReportPanel
          loading={isLoading}
          selectedIdea={selectedIdea}
          onDownloadReport={handleDownloadReport}
        />
      </Styled.IdeasContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessResultsRoot>
  );
}

BusinessResults.propTypes = {
  data: BusinessResultsData.isRequired,
  onComponentClick: PropTypes.func
};

export default BusinessResults;
